import { ProspectListStatus, type ProspectListRead } from '@signalvote/shared';
import { ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  askRangeLabel,
  BOOK_TIME_ZONES,
  bookQuery,
  timeZoneLabel,
  withFilters,
  type BookFilters,
} from '@/lib/fundraising/bookFilters';
import { US_STATE_OPTIONS } from '@/lib/usStates';

type Changes = Parameters<typeof withFilters>[1];

const STATES = US_STATE_OPTIONS.filter((option) => option.value !== '');

const wholeDollars = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

/** The Ask ranges on offer, as the URL writes their ends. "At the limit" joins them on a race with a ceiling. */
const ASK_RANGES: { minAsk: string | null; maxAsk: string | null }[] = [
  { minAsk: null, maxAsk: '999.99' },
  { minAsk: '1000', maxAsk: '2499.99' },
  { minAsk: '2500', maxAsk: null },
];

const capitalized = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

export interface BookNarrowingBarProps {
  filters: BookFilters;
  /** The campaign's uploads; only those that finished bringing people in are offered. */
  lists: readonly Pick<ProspectListRead, 'id' | 'name' | 'status'>[];
  /** The most any Ask may be (a federal race's limit), or null. */
  askCeiling: number | null;
  onChange: (changes: Changes) => void;
}

/**
 * The book's second row of filters: an uploaded list, a place (state, city, the start of a ZIP), a time
 * zone (read from each person's address) and an Ask range. Each combines with the search, the segment, Match and the call book, on the server. Presentational.
 */
export function BookNarrowingBar({ filters, lists, askCeiling, onChange }: BookNarrowingBarProps) {
  const { minAsk, maxAsk } = bookQuery(filters);
  const offered = lists
    .filter((list) => list.status === ProspectListStatus.Imported)
    .sort((a, b) => a.name.localeCompare(b.name));
  const listName = filters.list ? (lists.find((list) => list.id === filters.list)?.name ?? 'That list') : 'Any';
  const stateName = filters.state ? (STATES.find((s) => s.value === filters.state)?.label ?? filters.state) : 'Any';
  const atTheLimit = askCeiling !== null && minAsk === askCeiling && maxAsk === null;
  const askName = atTheLimit ? 'At the limit' : (askRangeLabel(minAsk, maxAsk) ?? 'any');

  return (
    <div className="flex flex-wrap items-center gap-3">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="h-10 text-[13px]" disabled={offered.length === 0 && !filters.list}>
            List: {listName}
            <ChevronDown className="ml-1.5 h-4 w-4" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="max-h-72 overflow-y-auto">
          <DropdownMenuItem onSelect={() => onChange({ list: null })}>Any</DropdownMenuItem>
          {offered.map((list) => (
            <DropdownMenuItem key={list.id} onSelect={() => onChange({ list: list.id })}>
              {list.name}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="h-10 text-[13px]">
            State: {stateName}
            <ChevronDown className="ml-1.5 h-4 w-4" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="max-h-72 overflow-y-auto">
          <DropdownMenuItem onSelect={() => onChange({ state: null })}>Any</DropdownMenuItem>
          {STATES.map((state) => (
            <DropdownMenuItem key={state.value} onSelect={() => onChange({ state: state.value })}>
              {state.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <Input
        value={filters.city ?? ''}
        onChange={(e) => onChange({ city: e.target.value })}
        placeholder="City"
        aria-label="City"
        className="h-10 w-full text-[13px] sm:w-40"
      />
      <Input
        value={filters.zip ?? ''}
        onChange={(e) => onChange({ zip: e.target.value.replace(/[^\d-]/g, '') })}
        placeholder="ZIP"
        aria-label="ZIP"
        inputMode="numeric"
        className="h-10 w-full text-[13px] sm:w-28"
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="h-10 text-[13px]">
            Time zone: {filters.timeZone ? timeZoneLabel(filters.timeZone) : 'Any'}
            <ChevronDown className="ml-1.5 h-4 w-4" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem onSelect={() => onChange({ timeZone: null })}>Any</DropdownMenuItem>
          {BOOK_TIME_ZONES.map((zone) => (
            <DropdownMenuItem key={zone} onSelect={() => onChange({ timeZone: zone })}>
              {timeZoneLabel(zone)}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="h-10 text-[13px]">
            Ask: {capitalized(askName)}
            <ChevronDown className="ml-1.5 h-4 w-4" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem onSelect={() => onChange({ minAsk: null, maxAsk: null })}>Any</DropdownMenuItem>
          {ASK_RANGES.map((range) => (
            <DropdownMenuItem key={`${range.minAsk}-${range.maxAsk}`} onSelect={() => onChange(range)}>
              {capitalized(askRangeLabel(range.minAsk === null ? null : Number(range.minAsk), range.maxAsk === null ? null : Number(range.maxAsk))!)}
            </DropdownMenuItem>
          ))}
          {askCeiling !== null && (
            <DropdownMenuItem onSelect={() => onChange({ minAsk: String(askCeiling), maxAsk: null })}>
              At the limit ({wholeDollars.format(askCeiling)})
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
